import { createContext, useContext, useState } from "react";
import { Card, CardBody, Col, Container, Label, Row, Form } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { jwtDecode, JwtPayload } from "jwt-decode";
import * as Yup from "yup";
import { useFormik } from "formik";
import { login } from "Api/UserApi";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import logoLight from "../../assets/images/logo-light.png";
import { toast } from "react-toastify";
import BaseInput from "Components/Base/BaseInput";
import withRouter from "Components/Common/withRouter";
import BaseButton from "Components/Base/BaseButton";
import {
  checkStatusCodeSuccess,
  labels,
  loginLabels,
} from "Components/constants/Common";
import {
  emailRegex,
  InputPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { HttpStatusCodes } from "helpers/Enums/Enums";
import { setItem } from "Components/constants/SessionStores";
import { getUserPermission } from "Api/common";
import { PermissionsContext } from "./PermissiosnProvider";

interface CustomJwtPayload extends JwtPayload {
  role: string;
  id: string;
  authUser: string;
}

const Login = (props: any) => {
  document.title = loginLabels.login;

  const navigate = useNavigate();
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error(
      "PermissionsComponent must be used within a PermissionsProvider"
    );
  }
  const { updatePermissions } = context;
  const [loader, setLoader] = useState<boolean>(false);

  const validation: any = useFormik({
    enableReinitialize: true,

    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required(validationMessages.required(loginLabels.Email))
        .matches(emailRegex, validationMessages.format(loginLabels.Email)),
      password: Yup.string().required(
        validationMessages.required(loginLabels.Password)
      ),
    }),
    onSubmit: async (values) => {
      setLoader(true);
      const payload = {
        email: values.email,
        password: values.password,
      };

      await login(payload)
        .then(async (res) => {
          if (
            res?.statusCode &&
            HttpStatusCodes.SUCCESS.includes(res?.statusCode)
          ) {
            const token = res?.data?.token;
            const decodedToken = jwtDecode<CustomJwtPayload>(token);
            const role = decodedToken.role;
            const id = decodedToken.id;
            const authUser = {
              id,
              role,
              token,
            };

            setItem(loginLabels.authUser, JSON.stringify(authUser));
            setItem(loginLabels.token, token);
            setItem(loginLabels.Role, role);
            setItem(loginLabels.Id, id);
            await getUserPermission(Number(id))
              .then((resp) => {
                if (checkStatusCodeSuccess(resp?.statusCode)) {
                  setItem("permissions", JSON.stringify(resp?.data));
                  updatePermissions(resp?.data);
                  navigate("/dashboard");
                }
              })
              .catch((err) => {
                return err;
              })
              .finally(() => {
                setLoader(false);
              });
            toast.success(res?.message);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message || error?.message);
          setLoader(false);
        })
        .finally(() => setLoader(false));
    },
  });

  return (
    <ParticlesAuth>
      <div className="auth-page-content">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-4 text-white-50">
                <div>
                  <Link to="/" className="d-inline-block auth-logo">
                  {/*For Future Purpose*/}
                    {/* <img src={logoLight} alt="" height="20" /> */}
                  </Link>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card>
                <CardBody className="p-4">
                  
                  <div className="text-center mt-0">
                    <img src={logoLight} alt="" height="35" width="43" />
                    <h5 className="text-success mt-2">{labels.welcome}</h5>
                  </div>
                  <div className="p-2 mt-3">
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <div className="mb-3">
                        <BaseInput
                          label={loginLabels.Email}
                          name={loginLabels.email}
                          className="form-control"
                          placeholder={InputPlaceHolder(loginLabels.Email)}
                          type="text"
                          onChange={validation.handleChange}
                          handleBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          touched={validation.touched.email}
                          error={validation.errors.email}
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <div className="position-relative auth-pass-inputgroup mb-3">
                          <BaseInput
                            name={loginLabels.password}
                            label={loginLabels.Password}
                            value={validation.values.password || ""}
                            className="form-control pe-5"
                            placeholder={InputPlaceHolder(loginLabels.Password)}
                            onChange={validation.handleChange}
                            handleBlur={validation.handleBlur}
                            type="password"
                            error={validation.errors.password}
                            touched={validation.touched.password}
                            required
                            rightSideText={
                              <Link to="/verify-email" className="text-muted">
                                {loginLabels.forgotPasswordLabel}?
                              </Link>
                            }
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <BaseButton
                          color="success"
                          type="submit"
                          label={loginLabels.signIn}
                          loader={loader}
                          className="w-100"
                        />
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default withRouter(Login);
